import {
  addDays,
  includesMember,
  publicHolidayOn,
  schoolHolidayOn,
  type Region,
} from '@shh/shared';
import { calendarText } from '../i18n/calendar';
import { locale } from '../lib/locale';
import { eventsOn, type E } from './derive';
import type { FamilyData } from './family';

/**
 * The calendar's view of a day (Kalender 1, 1.24): the stored events and school spans of
 * `eventsOn`, plus what comes by itself – school holidays, public holidays and birthdays. Each
 * kind is a layer the family can switch off per device ("Was steht im Kalender?").
 */
export type Layer = 'holidays' | 'birthdays' | 'school' | 'shifts' | 'waste' | 'subscriptions';
export const LAYERS: Layer[] = [
  'holidays',
  'birthdays',
  'school',
  'shifts',
  'waste',
  'subscriptions',
];
export type Layers = Record<Layer, boolean>;
export const ALL_LAYERS: Layers = {
  holidays: true,
  birthdays: true,
  school: true,
  shifts: true,
  waste: true,
  subscriptions: true,
};

export const regionOf = (f: FamilyData): Region | undefined => f.household?.location?.region;

const layerOf = (e: E): Layer | null =>
  e.virtual === 'holiday'
    ? 'holidays'
    : e.virtual === 'birthday'
      ? 'birthdays'
      : e.virtual === 'timetable'
        ? 'school'
        : e.virtual === 'shift'
          ? 'shifts'
          : e.source === 'waste'
            ? 'waste'
            : e.source === 'ics'
              ? 'subscriptions'
              : null;

/** Holidays and birthdays of a day as all-day entries (never stored). */
export function extrasOn(f: FamilyData, date: string): E[] {
  const out: E[] = [];
  const region = regionOf(f);
  if (region) {
    const ferien = schoolHolidayOn(region, date, locale);
    if (ferien)
      out.push({
        id: `hol_${ferien.start}`,
        title: ferien.name,
        icon: 'plane',
        date: ferien.start,
        endDate: ferien.end,
        assignees: 'all',
        bring: [],
        source: 'manual',
        virtual: 'holiday',
      });
    const feiertag = publicHolidayOn(region, date, locale);
    if (feiertag)
      out.push({
        id: `ph_${date}`,
        title: feiertag,
        icon: 'star',
        date,
        assignees: 'all',
        bring: [],
        source: 'manual',
        virtual: 'holiday',
        publicHoliday: true,
      });
  }
  const md = date.slice(5);
  for (const m of f.members) {
    if (m.birthday !== md) continue;
    out.push({
      id: `bd_${m.id}_${date}`,
      title: calendarText.birthdayOf(m.name),
      icon: 'cake',
      date,
      assignees: [m.id],
      bring: [],
      source: 'manual',
      virtual: 'birthday',
    });
  }
  // one-off jobs with a day stand on that day (Mike, 26.10.: "Einmal-Aufgaben mit Datum sollen
  // im Kalender am Tag stehen") – open to-dos and one-off tasks; without a day they stay in
  // "Heute zu tun"
  for (const t of f.todos) {
    if (t.done || t.due !== date) continue;
    out.push(jobEntry(`todo_${t.id}`, t.title, t.icon, date, t.assignees));
  }
  for (const t of f.tasks) {
    if (t.active === false || t.repeat.kind !== 'once' || t.repeat.date !== date) continue;
    out.push(jobEntry(`task_${t.id}`, t.title, t.icon, date, t.assignees));
  }
  return out;
}

const jobEntry = (
  id: string,
  title: string,
  icon: string,
  date: string,
  assignees: E['assignees'],
): E => ({
  id,
  title,
  icon,
  date,
  assignees,
  bring: [],
  source: 'manual',
  virtual: 'todo',
  details: [calendarText.oneOffJob],
});

export interface DayOptions {
  layers?: Layers;
  /** Only these people (and family events); undefined = everyone. */
  people?: string[];
}

/** Everything the calendar shows on a day, all-day entries first. */
export function calendarDay(
  f: FamilyData,
  date: string,
  { layers = ALL_LAYERS, people }: DayOptions = {},
): E[] {
  return [...extrasOn(f, date), ...eventsOn(f, date)].filter((e) => {
    const layer = layerOf(e);
    if (layer && !layers[layer]) return false;
    if (!people || e.virtual === 'holiday' || e.assignees === 'all') return true;
    return people.some((p) => includesMember(e.assignees, p));
  });
}

/** A day's entries split the way the day plan shows them: pinned on top, then by time. */
export function splitDay(list: E[]) {
  return { pinned: list.filter((e) => !e.start), timed: list.filter((e) => !!e.start) };
}

/** Monday of the week (ISO weeks start on Monday). */
export function weekStart(date: string): string {
  const wd = (new Date(`${date}T12:00:00Z`).getUTCDay() + 6) % 7;
  return addDays(date, -wd);
}

/** ISO calendar week number ("KW"). */
export function isoWeek(date: string): number {
  const thursday = addDays(weekStart(date), 3);
  const jan1 = `${thursday.slice(0, 4)}-01-01`;
  const days = Math.round((Date.parse(thursday) - Date.parse(jan1)) / 86_400_000);
  return Math.floor(days / 7) + 1;
}

/** First day of the month n months away, day clamped ("31.1. + 1" → "28.2."). */
export function addMonths(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const first = new Date(Date.UTC(y, m - 1 + n, 1));
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  first.setUTCDate(Math.min(d, last));
  return first.toISOString().slice(0, 10);
}

/** An event over several days (not school holidays – those are a band). */
export const isSpan = (e: E) => !!e.endDate && e.endDate > e.date && e.virtual !== 'holiday';

export interface Span {
  e: E;
  /** Index of the first shown day, length in days and lane (row) of the bar. */
  from: number;
  len: number;
  lane: number;
  /** Began before / goes on after the shown days – cut square at that side. */
  cutStart: boolean;
  cutEnd: boolean;
}

/**
 * Events over several days of a row of days as one bar each (Mike, 19.10.: "nicht jeden Tag
 * einzeln, sondern einmal durchgehend"), in lanes so they never overlap.
 */
export function daySpans(
  days: string[],
  lists: E[][],
  pick: (e: E) => boolean = () => true,
): Span[] {
  const first = days[0]!;
  const last = days[days.length - 1]!;
  const seen = new Map<string, E>();
  for (const list of lists) for (const e of list) if (isSpan(e) && pick(e)) seen.set(e.id, e);
  const lanes: number[] = [];
  return [...seen.values()]
    .sort(
      (a, b) => a.date.localeCompare(b.date) || (b.endDate ?? '').localeCompare(a.endDate ?? ''),
    )
    .map((e) => {
      const from = days.indexOf(e.date < first ? first : e.date);
      const to = days.indexOf(e.endDate! > last ? last : e.endDate!);
      let lane = lanes.findIndex((end) => end < from);
      if (lane < 0) lane = lanes.length;
      lanes[lane] = to;
      return {
        e,
        from,
        len: to - from + 1,
        lane,
        cutStart: e.date < first,
        cutEnd: e.endDate! > last,
      };
    });
}
